import React from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応（既存remotionコンポジション踏襲）
import { AbsoluteFill, Audio, Img, Sequence, Video, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { CaptionSegment } from '../../services/api/ugcAdService';
import { UgcAdCompositionScene, UgcAdCtaCard } from './types';
import { CAPTION_POSITION_CSS, CAPTION_STYLE_CSS, computeCaptionFontSize } from '../song-ad/captionPresets';
import { invalidateStableMediaUrl } from '../song-ad/stableMediaUrl';

/**
 * UGC動画広告のRemotion Composition。プレビュー/詳細編集の両方から@remotion/playerで使う。
 * song-ad の SongAdComposition を土台にしているが、音声の扱いが決定的に違う:
 *
 * - **クリップ音声をミュートしない。** UGCのトーク中心シーンは出演者が実際に話す映像
 *   （発話音声込みのクリップ）を生成する想定のため、`<Video muted>` にすると広告の主役の
 *   声が消える。song-ad は楽曲1トラックが正でクリップは無音素材扱い、という前提の違い。
 * - 楽曲トラック（audioUrl）は任意のBGM。クリップの発話と競合しないよう小さめの音量で流す。
 *
 * テロップ・CTAカードの描画は song-ad と同一（captionPresets を共用。プリセットの
 * フォントサイズ式は backend の書き出し側と一致している前提も同じ）。
 */
export interface UgcAdCompositionProps {
  scenes: UgcAdCompositionScene[];
  captions: CaptionSegment[];
  /** BGM URL（任意）。クリップの発話音声と重ねるため小音量で流す */
  bgmUrl: string | null;
  /** 末尾CTAカード。deriveUgcCtaCard の出力をそのまま渡す。無ければ描画しない */
  cta?: UgcAdCtaCard | null;
}

const BGM_VOLUME = 0.18;

const SceneVisual: React.FC<{ scene: UgcAdCompositionScene; durationInFrames: number }> = ({
  scene,
  durationInFrames,
}) => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, Math.max(1, durationInFrames)], [1, 1.08], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  if (scene.motionMode === 'clip' && scene.videoUrl) {
    return (
      <AbsoluteFill>
        <Video
          src={scene.videoUrl}
          // UGCは発話音声込みクリップが主役なのでミュートしない（ファイル冒頭コメント参照）
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => invalidateStableMediaUrl(scene.videoUrl)}
          pauseWhenBuffering
        />
      </AbsoluteFill>
    );
  }

  if (scene.imageUrl) {
    return (
      <AbsoluteFill style={{ overflow: 'hidden' }}>
        <Img
          src={scene.imageUrl}
          style={{ width: '100%', height: '100%', objectFit: 'cover', transform: `scale(${scale})` }}
          onError={() => invalidateStableMediaUrl(scene.imageUrl)}
        />
      </AbsoluteFill>
    );
  }

  return <AbsoluteFill style={{ backgroundColor: '#1D1927' }} />;
};

const CaptionOverlay: React.FC<{ caption: CaptionSegment; fps: number; width: number; height: number; ctaStart?: number }> = ({
  caption,
  fps,
  width,
  height,
  ctaStart,
}) => {
  const frame = useCurrentFrame();
  const startFrame = Math.round(caption.start * fps);
  const endFrame = Math.round(caption.end * fps);
  if (frame < startFrame || frame >= endFrame) return null;
  const position = ctaStart !== undefined && frame >= Math.round(ctaStart * fps) ? 'bottom' : caption.position;

  const fontSize = computeCaptionFontSize(width, height, caption.size);
  const lines = caption.text.split('\n').filter((line) => line.length > 0);

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: fontSize * 0.15,
        padding: '0 7%',
        pointerEvents: 'none',
        ...CAPTION_POSITION_CSS[position],
      }}
    >
      {lines.map((line, index) => (
        <span
          key={index}
          style={{
            display: 'inline-block',
            fontSize,
            lineHeight: 1.4,
            textAlign: 'center',
            whiteSpace: 'pre-wrap',
            ...CAPTION_STYLE_CSS[caption.style],
          }}
        >
          {line}
        </span>
      ))}
    </div>
  );
};

const CTA_CARD_FONT_HEIGHT_RATIO = 0.058;

const CtaCardOverlay: React.FC<{ cta: UgcAdCtaCard; width: number; height: number }> = ({ cta, width, height }) => {
  const fontSize = Math.max(16, Math.round(height * CTA_CARD_FONT_HEIGHT_RATIO * 1.3));
  const logoWidth = Math.round(width * 0.45);

  return (
    <AbsoluteFill>
      {cta.backgroundImageUrl ? (
        <Img
          src={cta.backgroundImageUrl}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(14px) brightness(0.82) saturate(0.55)',
            transform: 'scale(1.06)',
          }}
          onError={() => invalidateStableMediaUrl(cta.backgroundImageUrl)}
        />
      ) : (
        <AbsoluteFill style={{ backgroundColor: '#1D1927' }} />
      )}
      <AbsoluteFill style={{ backgroundColor: 'rgba(0,0,0,.25)' }} />
      <AbsoluteFill
        style={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 10%',
          gap: fontSize * 0.6,
        }}
      >
        {cta.logoUrl && (
          <Img
            src={cta.logoUrl}
            style={{ width: logoWidth, maxHeight: height * 0.28, objectFit: 'contain' }}
            onError={() => invalidateStableMediaUrl(cta.logoUrl)}
          />
        )}
        <span
          style={{
            display: 'inline-block',
            fontSize,
            fontWeight: 700,
            lineHeight: 1.4,
            textAlign: 'center',
            color: '#fff',
            letterSpacing: '.04em',
            textShadow: '0 2px 14px rgba(0,0,0,.6), 0 0 3px rgba(0,0,0,.5)',
          }}
        >
          {cta.text}
        </span>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const UgcAdComposition: React.FC<UgcAdCompositionProps> = ({ scenes, captions, bgmUrl, cta }) => {
  const { fps, durationInFrames, width, height } = useVideoConfig();

  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      {scenes.map((scene) => {
        const from = Math.max(0, Math.round(scene.startSec * fps));
        const to = Math.min(durationInFrames, Math.round(scene.endSec * fps));
        const duration = to - from;
        if (duration <= 0) return null;
        return (
          <Sequence key={scene.id} from={from} durationInFrames={duration}>
            <SceneVisual scene={scene} durationInFrames={duration} />
          </Sequence>
        );
      })}

      {cta && (
        <Sequence
          from={Math.max(0, Math.round(cta.start * fps))}
          durationInFrames={Math.max(1, Math.round((cta.end - cta.start) * fps))}
        >
          <CtaCardOverlay cta={cta} width={width} height={height} />
        </Sequence>
      )}

      {/* CTAの背景より手前に描き、末尾のセリフ字幕も残す。 */}
      {captions.map((caption) => (
        <CaptionOverlay key={caption.id} caption={caption} fps={fps} width={width} height={height} ctaStart={cta?.start} />
      ))}

      {bgmUrl && (
        <Audio
          src={bgmUrl}
          volume={BGM_VOLUME}
          onError={() => invalidateStableMediaUrl(bgmUrl)}
          pauseWhenBuffering
        />
      )}
    </AbsoluteFill>
  );
};

export default UgcAdComposition;
